import { MapPin, CalendarDays, Sparkles, Loader2, Dices } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";

export const BUDGETS = [
  { value: "$", label: "$", hint: "Easygoing" },
  { value: "$$", label: "$$", hint: "Mid-range" },
  { value: "$$$", label: "$$$", hint: "Splurge" },
];
export const VIBES = ["Casual", "Upscale", "Live Music", "Dancing"];

export function todayISO() {
  return new Date().toISOString().split("T")[0];
}

function FieldLabel({ children }) {
  return <label className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">{children}</label>;
}

function BudgetPicker({ value, onChange }) {
  return (
    <div data-testid="budget-selector" className="mt-2 grid grid-cols-3 gap-3">
      {BUDGETS.map((b) => (
        <button
          key={b.value}
          data-testid={`budget-${b.value.length}`}
          onClick={() => onChange(b.value)}
          className={`rounded-xl border px-3 py-3 text-center transition-colors ${
            value === b.value
              ? "border-transparent cta-gradient text-white"
              : "border-border bg-secondary/40 hover:border-primary/50 text-foreground"
          }`}
        >
          <div className="font-display text-lg font-extrabold">{b.label}</div>
          <div className={`text-[11px] ${value === b.value ? "text-white/80" : "text-muted-foreground"}`}>{b.hint}</div>
        </button>
      ))}
    </div>
  );
}

function VibePicker({ value, onChange }) {
  return (
    <div data-testid="vibe-selector" className="mt-2 flex flex-wrap gap-3">
      {VIBES.map((v) => (
        <button
          key={v}
          data-testid={`vibe-${v.replace(/\s+/g, "-").toLowerCase()}`}
          onClick={() => onChange(v)}
          className={`rounded-full border px-5 py-2.5 text-sm font-semibold transition-colors ${
            value === v
              ? "border-transparent bg-foreground text-background"
              : "border-border bg-secondary/40 hover:border-primary/50 text-foreground"
          }`}
        >
          {v}
        </button>
      ))}
    </div>
  );
}

export function PlannerForm({ form, onChange, onPlan, onSurprise, loading, surprising }) {
  const set = (key) => (value) => onChange({ ...form, [key]: value });
  const busy = loading || surprising;
  return (
    <>
      <div className="grid gap-5 sm:grid-cols-2">
        <div>
          <FieldLabel>City</FieldLabel>
          <div className="mt-2 relative">
            <MapPin size={17} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              data-testid="city-input"
              value={form.city}
              onChange={(e) => set("city")(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && onPlan()}
              placeholder="e.g. Austin"
              className="h-12 pl-10 rounded-xl"
            />
          </div>
        </div>
        <div>
          <FieldLabel>Date</FieldLabel>
          <div className="mt-2 relative">
            <CalendarDays size={17} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              data-testid="date-picker"
              type="date"
              value={form.date}
              min={todayISO()}
              onChange={(e) => set("date")(e.target.value)}
              className="h-12 pl-10 rounded-xl"
            />
          </div>
        </div>
      </div>

      <div className="mt-6">
        <FieldLabel>Budget</FieldLabel>
        <BudgetPicker value={form.budget} onChange={set("budget")} />
      </div>

      <div className="mt-6">
        <FieldLabel>Vibe</FieldLabel>
        <VibePicker value={form.vibe} onChange={set("vibe")} />
      </div>

      <Button
        data-testid="plan-my-night-btn"
        onClick={onPlan}
        disabled={busy}
        className="mt-8 h-14 w-full rounded-2xl cta-gradient text-base font-bold text-white transition-transform hover:scale-[1.01] active:scale-[0.99]"
      >
        {loading ? <Loader2 size={20} className="mr-2 animate-spin" /> : <Sparkles size={18} className="mr-2" />}
        {loading ? "Building your night…" : "Plan My Night"}
      </Button>

      <button
        data-testid="surprise-me-btn"
        onClick={onSurprise}
        disabled={busy}
        className="mt-4 flex h-12 w-full items-center justify-center gap-2 rounded-2xl border border-dashed border-border bg-secondary/30 text-sm font-semibold text-muted-foreground transition-colors hover:border-accent hover:text-foreground disabled:opacity-60"
      >
        {surprising ? <Loader2 size={17} className="animate-spin" /> : <Dices size={17} className="neon-cyan" />}
        {surprising ? "Rolling the dice…" : "Surprise Me — random city, vibe & date"}
      </button>
    </>
  );
}
